'use client';

import { createContext, JSX, PropsWithChildren, useCallback, useEffect } from 'react';
import { useEvent, useSetState } from 'react-use';
import { AudioStockItem } from 'search-gateway-client';

import { AUDIO_EVENTS, PauseEvent, PlayEvent } from '../../../events/AudioEvents';

export interface ActiveAudioItemState {
    stockItem: AudioStockItem | null;
    isPlaying: boolean;
}

export interface ActiveAudioItemContext {
    activeAudioItemState: ActiveAudioItemState;
    setActiveAudioItemState: (state: ActiveAudioItemState) => void;
}

export const ActiveAudioItemContext = createContext<ActiveAudioItemContext>({
    activeAudioItemState: { stockItem: null, isPlaying: false },
    setActiveAudioItemState: () => {},
});

export const ActiveAudioItemProvider = ({ children }: PropsWithChildren): JSX.Element => {
    const [activeAudioItemState, _setActiveAudioItemState] = useSetState<ActiveAudioItemState>({
        stockItem: null,
        isPlaying: false,
    });

    const setActiveAudioItemState = useCallback(
        ({ stockItem, isPlaying }: Partial<ActiveAudioItemState>) => {
            // TODO: add analytics events when active item changes

            _setActiveAudioItemState({ stockItem, isPlaying });
        },
        [_setActiveAudioItemState],
    );

    useEvent(AUDIO_EVENTS.ENDED, () => {
        _setActiveAudioItemState({ isPlaying: false });
    });

    useEffect(() => {
        if (!activeAudioItemState?.stockItem) return;
        // In order to sync the play/pause state between the global audio player and the stock item card,
        // we need to dispatch the events in response to the active audio item change.
        // Otherwise, the src url won't update at the same time as the play/pause state.
        if (activeAudioItemState.isPlaying) {
            window.dispatchEvent(new PlayEvent(activeAudioItemState.stockItem.previewUrl));
        } else {
            window.dispatchEvent(new PauseEvent(activeAudioItemState.stockItem.previewUrl));
        }
    }, [activeAudioItemState]);

    return (
        <ActiveAudioItemContext.Provider value={{ activeAudioItemState, setActiveAudioItemState }}>
            {children}
        </ActiveAudioItemContext.Provider>
    );
};
